'use client';

import { useState } from 'react';

import Image from 'next/image';

import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { publicStorageUrl } from '@kit/shared/storage';
import { formatLocaleDate, formatTimeToAmPm } from '@kit/shared/utils';
import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { Card, CardContent, CardHeader } from '@kit/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@kit/ui/tabs';

import { SeasonPriceBlock } from '~/components/class-seasons/season-price-block';
import { getSeasonUnavailableReason } from '~/lib/class-seasons/class-season-quote';

import {
  getClassSeasonCatalog,
  type ClassSeasonCatalogClass,
  type ClassSeasonCatalogTerm,
} from '../../_lib/class-season-catalog';
import type { ClassSeasonDetails } from '../../_lib/server/class-season.loader';
import { parseSeasonDate } from '../../_components/class-season-flow';

interface SeasonScheduleCatalogProps {
  seasons: ClassSeasonDetails[];
  onSelect: (seasonId: string) => void;
  locationImages?: Record<string, string[]>;
}

export function SeasonScheduleCatalog({
  seasons,
  onSelect,
  locationImages,
}: SeasonScheduleCatalogProps) {
  const { i18n } = useTranslation('bookings');

  return (
    <div className="space-y-3" data-test="season-schedule-catalog">
      {getClassSeasonCatalog(seasons).map((classGroup) => {
        const locationId = classGroup.terms[0]?.choices[0]?.season.locationId;
        const imageUrl = classGroup.imageUrl || (
          locationId ? locationImages?.[locationId]?.[0] : undefined
        );
        const className = i18n.language === 'ar' && classGroup.classNameAlt
          ? classGroup.classNameAlt : classGroup.className;
        const description = i18n.language === 'ar' && classGroup.descriptionAlt
          ? classGroup.descriptionAlt : classGroup.description;

        return (
          <Card key={classGroup.classTypeId} data-test="season-catalog-class">
            <CardHeader className="flex-row items-start gap-3 space-y-0 p-4 pb-3">
              {imageUrl ? (
                <Image
                  src={publicStorageUrl(imageUrl)}
                  alt={className}
                  width={64}
                  height={64}
                  className="h-16 w-16 shrink-0 rounded-lg object-cover"
                />
              ) : null}
              <div className="min-w-0 space-y-1">
                <h3 className="text-base leading-snug font-semibold">{className}</h3>
                {description ? <p className="text-muted-foreground line-clamp-2 text-sm">{description}</p> : null}
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-1">
              <ClassTerms classGroup={classGroup} onSelect={onSelect} />
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function ClassTerms({
  classGroup,
  onSelect,
}: {
  classGroup: ClassSeasonCatalogClass;
  onSelect: (seasonId: string) => void;
}) {
  const { i18n } = useTranslation('bookings');
  const [selectedTerm, setSelectedTerm] = useState(classGroup.terms[0]?.id);
  const activeTerm = classGroup.terms.some((term) => term.id === selectedTerm)
    ? selectedTerm : classGroup.terms[0]?.id;
  const formatDate = (date: string, withYear = false) => formatLocaleDate(
    parseSeasonDate(date), i18n.language,
    { month: 'short', day: 'numeric', year: withYear ? 'numeric' : undefined },
  );

  return (
    <Tabs value={activeTerm} onValueChange={setSelectedTerm} dir={i18n.dir()} className="min-w-0">
      <div className="overflow-x-auto pb-2">
        <TabsList variant="segmented" className="h-auto w-max gap-1 rounded-lg p-1">
          {classGroup.terms.map((term) => (
            <TabsTrigger
              key={term.id}
              value={term.id}
              className="rounded-md px-3 py-1.5 text-xs data-[state=active]:shadow-none"
              data-test="season-term-tab"
            >
              {formatDate(term.startsOn)} – {formatDate(term.endsOn, true)}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
      {classGroup.terms.map((term) => (
        <TabsContent key={term.id} value={term.id} className="mt-0">
          <SeasonTimes term={term} onSelect={onSelect} />
        </TabsContent>
      ))}
    </Tabs>
  );
}

function SeasonTimes({
  term,
  onSelect,
}: {
  term: ClassSeasonCatalogTerm;
  onSelect: (seasonId: string) => void;
}) {
  const { t, i18n } = useTranslation('bookings');
  const schedules = [...new Map(
    term.choices.map((choice) => [choice.season.classScheduleId, choice.season]),
  ).values()];
  const weekday = (day: number) => new Intl.DateTimeFormat(i18n.language, {
    weekday: 'short', timeZone: 'UTC',
  }).format(new Date(Date.UTC(2026, 0, 4 + day, 12)));

  if (schedules.length === 0) {
    return <p className="text-muted-foreground py-3 text-sm">{t('season.unavailable.no_sessions')}</p>;
  }

  return (
    <div className="divide-y" data-test="season-times">
      {schedules.map((season) => {
        const times = term.choices
          .filter((choice) => choice.season.classScheduleId === season.classScheduleId)
          .map((choice) => `${weekday(choice.weekday)} · ${formatTimeToAmPm(choice.startTime, i18n.language)}`)
          .join(' / ');

        return (
          <Button
            key={season.classScheduleId}
            type="button"
            variant="ghost"
            className="h-auto min-h-20 w-full items-center justify-between gap-3 px-2 py-3 text-start whitespace-normal"
            data-test="season-time-option"
            onClick={() => onSelect(season.classScheduleId)}
          >
            <span className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-sm font-medium">{times}</span>
              {season.scheduleName ? <span className="text-muted-foreground text-xs font-normal">{season.scheduleName}</span> : null}
              {getSeasonUnavailableReason(season.quote) === 'full' ? (
                <Badge variant="warning" className="w-fit">{t('season.full')}</Badge>
              ) : null}
            </span>
            <SeasonPriceBlock quote={season.quote} compact />
            <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0 rtl:rotate-180" />
          </Button>
        );
      })}
    </div>
  );
}
